import { useEffect, useState } from 'react';
import { firmCatalogApi, type FirmChange } from '../../lib/api';
import { diffFirm } from '../../lib/firmDiff';
import { ICE, TEAL, ACCENT, FIELD, PANEL, WIN, LOSS, PX_FONT, TITLE_FONT, pixelFrame } from './theme';

/* Oversight for prop-firm rule changes: every edit (the weekly check's automatic updates and admin
   edits) with what changed from what to what. Admins see a notice on Home Base when new ones land. */

const SEEN_KEY = 'paladin-rule-changes-seen';
const label = { fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.12em', color: TEAL } as const;

export const markRuleChangesSeen = () => { try { localStorage.setItem(SEEN_KEY, new Date().toISOString()); } catch { /* per-page only */ } };
const lastSeen = () => { try { return localStorage.getItem(SEEN_KEY); } catch { return null; } };

export function useRuleChanges(username: string, enabled: boolean) {
  const [log, setLog] = useState<FirmChange[]>([]);
  useEffect(() => { if (enabled) firmCatalogApi.log(username).then(setLog).catch(() => undefined); }, [username, enabled]);
  const seen = lastSeen();
  const unseen = log.filter(c => !seen || c.changed_at > seen);
  return { log, unseen };
}

export function RuleChangesFeed({ log }: { log: FirmChange[] }) {
  if (!log.length) return <div style={label}>No rule changes yet.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {log.map(c => {
        const diffs = diffFirm(c.before, c.after);
        const auto = (c.changed_by ?? '').toLowerCase().includes('weekly');
        return (
          <div key={c.id} style={{ background: FIELD, borderRadius: 'var(--pal-radius, 0)', padding: 14, boxShadow: `inset 3px 0 0 ${auto ? ACCENT : '#4C8DFF'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ fontFamily: TITLE_FONT, fontWeight: 800, fontSize: 15, color: ICE }}>{c.firm_name ?? c.firm_id}</span>
              <span style={label}>{auto ? 'Automatic · weekly rules check' : `Edited by ${c.changed_by ?? '—'}`} · {new Date(c.changed_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
            </div>
            {diffs.length === 0 ? <div style={label}>Re-verified, no value changes.</div> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) auto', gap: '4px 14px' }}>
                {diffs.slice(0, 30).map((d, i) => (
                  <div key={i} style={{ display: 'contents' }}>
                    <span style={{ fontFamily: PX_FONT, fontSize: 12, color: TEAL }}>{d.where}</span>
                    <span style={{ fontFamily: PX_FONT, fontSize: 12, color: ICE }}>{d.field}</span>
                    <span style={{ fontFamily: PX_FONT, fontSize: 12, whiteSpace: 'nowrap' }}><span style={{ color: LOSS, textDecoration: 'line-through' }}>{d.from}</span> → <b style={{ color: WIN }}>{d.to}</b></span>
                  </div>
                ))}
                {diffs.length > 30 && <span style={label}>…and {diffs.length - 30} more</span>}
              </div>
            )}
            {c.note && <div style={{ ...label, marginTop: 8, lineHeight: 1.5, textTransform: 'none', letterSpacing: '0.02em' }}>{c.note}</div>}
          </div>
        );
      })}
    </div>
  );
}

/** Home Base notice for admins/mods when firm rules changed since they last looked */
export function RuleChangesNotice({ count, onOpen }: { count: number; onOpen: () => void }) {
  if (!count) return null;
  return (
    <button type="button" onClick={onOpen}
      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: 16, border: 'none', cursor: 'pointer', textAlign: 'left',
        background: PANEL, boxShadow: pixelFrame(true), borderRadius: 'var(--pal-radius, 0)' }}>
      <span className="pal-pulse" style={{ width: 8, height: 8, borderRadius: 4, background: ACCENT }} />
      <span style={{ fontFamily: PX_FONT, fontSize: 13, color: ICE }}><b>{count} prop-firm rule change{count === 1 ? '' : 's'}</b> since you last looked</span>
      <span style={{ ...label, marginLeft: 'auto', color: ACCENT }}>Review →</span>
    </button>
  );
}
